// Color-derivation helpers for the custom widget.
//
// deriveInteractiveColors(base) → { hover, text }
//   For filled interactive elements (buttons, link/tab text). Hover shifts
//   lightness toward the opposite end so the state change is always visible;
//   text picks whichever of #ffffff / #1a1a1a has higher WCAG contrast.
//
// deriveSurfaceColors(base) → { border, text, placeholder }
//   For input / card surfaces where `base` is the background. Border is a
//   subtle shift, text uses the same contrast pick, placeholder is a faded
//   text-toward-bg blend that stays readable on either light or dark bases.

import { colord, extend } from 'colord';
import a11yPlugin from 'colord/plugins/a11y';
import mixPlugin from 'colord/plugins/mix';

extend([a11yPlugin, mixPlugin]);

const HOVER_SHIFT = 0.08;
const SURFACE_SHIFT = 0.10;
const PLACEHOLDER_MIX = 0.5;
const TEXT_CANDIDATES = ['#ffffff', '#1a1a1a'];

const pickContrastText = (c) =>
  TEXT_CANDIDATES.reduce((best, candidate) =>
    c.contrast(candidate) > c.contrast(best) ? candidate : best
  );

const deriveInteractiveColors = (baseColor) => {
  const c = colord(baseColor);
  if (!c.isValid()) {
    return { hover: baseColor, text: '#ffffff' };
  }

  const hover = c.isLight()
    ? c.darken(HOVER_SHIFT).toHex()
    : c.lighten(HOVER_SHIFT).toHex();

  return { hover, text: pickContrastText(c) };
};

export const deriveSurfaceColors = (baseColor) => {
  const c = colord(baseColor);
  if (!c.isValid()) {
    return { border: baseColor, text: '#1a1a1a', placeholder: '#888888' };
  }

  const border = c.isLight()
    ? c.darken(SURFACE_SHIFT).toHex()
    : c.lighten(SURFACE_SHIFT).toHex();
  const text = pickContrastText(c);
  const placeholder = colord(text).mix(c, PLACEHOLDER_MIX).toHex();

  return { border, text, placeholder };
};

export default deriveInteractiveColors;
